// MF-551 — Card 'demerit-alert' (Al-Taqwa): students who reached a demerit-points band this school year.
// The server sends every student at or above the lowest band, with the demerits behind each one; the
// filters (category, year levels, band) and the detail of a student all work on that answer, so changing
// them never asks the server for anything. Staff only (the item says so; the server enforces it).
import { useMemo, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material'
import {
  SERIES_COLORS,
  SUMMARY_PALETTE,
  TONE_COLORS,
  TONE_PILL,
  baseTextStyle,
  tooltipBase
} from '../boardTheme'

const num = (v) => (v === null || v === undefined ? null : Number(v))
const orDash = (v) => (v === null || v === undefined || v === '' ? '–' : v)
const parseHistory = (h) => {
  try {
    return JSON.parse(h || '[]')
  } catch {
    return []
  }
}
// Year levels come as Maestro names ("Year 12", "Prep", "Kinder"): early years first, then Year 1..12
const EARLY = ['early learning', 'kinder', 'prep']
const yearRank = (y) => {
  const name = String(y).toLowerCase()
  if (EARLY.includes(name)) return EARLY.indexOf(name) - 10
  const n = parseInt(name.replace(/\D+/g, ''), 10)
  return Number.isFinite(n) ? n : 100
}
const byYearOrder = (a, b) => yearRank(a) - yearRank(b) || String(a).localeCompare(String(b))
const yearLabel = (y) => (/^\d+$/.test(String(y)) ? `Year ${y}` : String(y))
const inBand = (points, band) => points >= band.min && (band.max === null || points <= band.max)

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
// The two thresholds the school acts on (MF-584: per category, never summed). The server sends them in options.escalation.
const FALLBACK_ESCALATION = { contract: 30, expulsion: 32 }
// What the school's own colour code means: a contract still running is red, one past its follow-up date orange. Both stay readable
// next to the closed palette (orange = needs attention). A contract with no follow-up date is still active (the school said so).
const CONTRACT_CHIP = {
  active: { label: 'Active', color: '#b3261e', bg: '#fbe3e0' },
  expired: { label: 'Expired', color: '#7a3e12', bg: '#f6dcc2' }
}
const LEVEL_CHIP = {
  expulsion: { label: 'Expulsion level', color: '#b3261e', bg: '#fbe3e0' },
  contract: { label: 'Contract level', color: '#7a3e12', bg: '#f6dcc2' }
}
const AXIS = { axisLabel: { ...baseTextStyle, fontSize: 11 }, nameTextStyle: baseTextStyle }

const Chart = ({ title, option, empty }) => (
  <Box sx={{ flex: '1 1 320px', minWidth: 0, minHeight: 220, display: 'flex', flexDirection: 'column' }}>
    <Typography sx={{ fontSize: 12, fontWeight: 700, color: SUMMARY_PALETTE.ink, mb: 0.5 }}>
      {title}
    </Typography>
    {empty ? (
      <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted, p: 2 }}>
        No data for these filters.
      </Typography>
    ) : (
      <ReactECharts option={option} notMerge style={{ height: '100%', minHeight: 200, width: '100%' }} />
    )}
  </Box>
)

// "12-05-2026" → Date
const parseDay = (d) => {
  const [dd, mm, yy] = String(d).split('-').map(Number)
  return new Date(yy, mm - 1, dd)
}

const HEAD = { fontSize: 11, fontWeight: 700, color: SUMMARY_PALETTE.ink, whiteSpace: 'nowrap', py: 0.75 }
const CELL = { fontSize: 12, color: SUMMARY_PALETTE.ink, py: 0.5 }

const DemeritAlertCard = ({ data }) => {
  const options = data?.meta?.card?.options || {}
  const bands = options.bands || []
  const categories = useMemo(() => options.categories || [], [options.categories])
  const allRows = useMemo(() => data?.rows || [], [data])
  const rows = useMemo(() => allRows.filter((r) => r.row_kind === 'student'), [allRows])
  const zeroRows = useMemo(() => allRows.filter((r) => r.row_kind === 'zero'), [allRows])
  const monthRows = useMemo(() => allRows.filter((r) => r.row_kind === 'month'), [allRows])

  const [category, setCategory] = useState(categories[0] || '')
  const escalation = options.escalation?.[category] || FALLBACK_ESCALATION
  const [years, setYears] = useState([])
  const [bandCode, setBandCode] = useState(bands[0]?.code || '')
  const [detail, setDetail] = useState(null)
  const [view, setView] = useState('table')

  const yearLevels = useMemo(
    () =>
      [
        ...new Set(rows.map((r) => r.year_level).filter((y) => y !== null && y !== undefined && y !== ''))
      ].sort(byYearOrder),
    [rows]
  )
  const band = bands.find((b) => b.code === bandCode) || bands[0]
  const shown = useMemo(
    () =>
      band
        ? rows.filter(
            (r) =>
              r.category === category &&
              inBand(num(r.points), band) &&
              (years.length === 0 || years.includes(r.year_level))
          )
        : [],
    [rows, category, band, years]
  )

  // Charts: the category and band chosen; MF-588 — the year-level filter narrows the charts too (none picked = all).
  const byYear = useMemo(() => {
    if (!band) return []
    const count = new Map()
    rows
      .filter(
        (r) =>
          r.category === category &&
          inBand(num(r.points), band) &&
          (years.length === 0 || years.includes(r.year_level))
      )
      .forEach((r) => count.set(r.year_level || 'Unknown', (count.get(r.year_level || 'Unknown') || 0) + 1))
    return [...count.entries()].sort((a, b) => byYearOrder(a[0], b[0]))
  }, [rows, category, band, years])

  // MF-588: the trend shows every category (SWB and YLC) side by side, this year solid and last year dashed.
  const monthly = useMemo(() => {
    const inLevels = monthRows.filter((r) => years.length === 0 || years.includes(r.year_level))
    const yearsPresent = [...new Set(monthRows.map((r) => Number(String(r.period).slice(0, 4))))].sort()
    const current = yearsPresent[yearsPresent.length - 1]
    const series = []
    categories.forEach((c, ci) => {
      const mine = inLevels.filter((r) => r.category === c)
      ;[current - 1, current]
        .filter((y) => yearsPresent.includes(y))
        .forEach((y) =>
          series.push({
            category: c,
            year: y,
            current: y === current,
            color: SERIES_COLORS[ci % SERIES_COLORS.length],
            axis: ci === 0 ? 0 : 1,
            // one row per month and year level: add up the levels picked
            values: MONTHS.map((_, i) => {
              const hits = mine.filter((r) => r.period === `${y}-${String(i + 1).padStart(2, '0')}`)
              return hits.length ? hits.reduce((n, r) => n + (num(r.points) || 0), 0) : null
            })
          })
        )
    })
    return { series, empty: inLevels.length === 0 }
  }, [monthRows, categories, years])

  const barOption = {
    textStyle: baseTextStyle,
    grid: { left: 40, right: 16, top: 16, bottom: 28 },
    tooltip: { ...tooltipBase, trigger: 'axis' },
    xAxis: { type: 'category', data: byYear.map(([y]) => yearLabel(y)), ...AXIS },
    yAxis: { type: 'value', minInterval: 1, name: 'Students', ...AXIS },
    series: [
      {
        type: 'bar',
        data: byYear.map(([, n]) => n),
        itemStyle: { color: SERIES_COLORS[0] },
        label: { show: true, position: 'top', ...baseTextStyle, fontSize: 11 }
      }
    ]
  }
  const lineOption = {
    textStyle: baseTextStyle,
    grid: { left: 52, right: categories.length > 1 ? 52 : 16, top: 32, bottom: 28 },
    tooltip: { ...tooltipBase, trigger: 'axis' },
    legend: { top: 0, textStyle: baseTextStyle },
    xAxis: { type: 'category', data: MONTHS, ...AXIS },
    // each category has its own scale (YLC has ~10x the points of SWB and would flatten it): left axis for the
    // first category, right axis for the others, each axis in its category's colour
    yAxis: categories.slice(0, 2).map((c, i) => ({
      type: 'value',
      name: `${c} points`,
      position: i === 0 ? 'left' : 'right',
      splitLine: { show: i === 0 },
      ...AXIS,
      axisLabel: { ...AXIS.axisLabel, color: SERIES_COLORS[i] },
      nameTextStyle: { ...AXIS.nameTextStyle, color: SERIES_COLORS[i] }
    })),
    series: monthly.series.map((s) => ({
      name: `${s.category} ${s.year}`,
      yAxisIndex: s.axis,
      type: 'line',
      smooth: false,
      connectNulls: false,
      data: s.values,
      lineStyle: { width: 2, ...(s.current ? {} : { type: 'dashed' }) },
      itemStyle: { color: s.color }
    }))
  }
  // One student's year: points added up demerit by demerit, with the thresholds the school acts on.
  const studentOption = useMemo(() => {
    const events = parseHistory(detail?.history)
      .map((h) => ({ at: parseDay(h.date), points: num(h.points) || 0 }))
      .sort((a, b) => a.at - b.at)
    let total = 0
    const data = events.map((e) => [e.at.getTime(), (total += e.points)])
    const showThresholds = total >= 20
    return {
      textStyle: baseTextStyle,
      grid: { left: 40, right: showThresholds ? 90 : 16, top: 16, bottom: 28 },
      tooltip: { ...tooltipBase, trigger: 'axis' },
      // dates, never raw numbers (a few demerits on the same day would otherwise label the axis with timestamps)
      xAxis: {
        type: 'time',
        ...AXIS,
        axisLabel: {
          ...AXIS.axisLabel,
          formatter: (v) => {
            const d = new Date(v)
            return `${d.getDate()} ${MONTHS[d.getMonth()]}`
          }
        }
      },
      yAxis: {
        type: 'value',
        name: 'Points',
        max: showThresholds ? Math.max(total + 2, 34) : undefined,
        ...AXIS
      },
      series: [
        {
          type: 'line',
          step: 'end',
          data,
          symbolSize: 6,
          itemStyle: { color: SERIES_COLORS[0] },
          markLine: showThresholds
            ? {
                silent: true,
                symbol: 'none',
                lineStyle: { color: TONE_COLORS.negative, type: 'dashed' },
                label: { ...baseTextStyle, fontSize: 10, formatter: (p) => p.data.label },
                data: [
                  { yAxis: escalation.contract, label: `${escalation.contract} · contract` },
                  { yAxis: escalation.expulsion, label: `${escalation.expulsion} · expulsion` }
                ]
              }
            : undefined
        }
      ]
    }
  }, [detail, escalation])
  const hasContract = shown.some((r) => r.contract_date)
  const hasPrevYear = shown.some((r) => r.prev_year_over)
  // students of the chosen category at each level (the band filter narrows the table, not this count)
  const levelCount = useMemo(
    () => ({
      contract: rows.filter((r) => r.category === category && r.escalation === 'contract').length,
      expulsion: rows.filter((r) => r.category === category && r.escalation === 'expulsion').length
    }),
    [rows, category]
  )

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, gap: 1.5 }}>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size='small' sx={{ minWidth: 120 }}>
          <InputLabel>Category</InputLabel>
          <Select label='Category' value={category} onChange={(e) => setCategory(e.target.value)}>
            {categories.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size='small' sx={{ minWidth: 180 }}>
          <InputLabel>Year level</InputLabel>
          <Select
            multiple
            label='Year level'
            value={years}
            onChange={(e) =>
              setYears(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)
            }
            renderValue={(sel) => (sel.length === 0 ? 'All' : sel.map(yearLabel).join(', '))}
          >
            {yearLevels.map((y) => (
              <MenuItem key={y} value={y}>
                <Checkbox size='small' checked={years.includes(y)} />
                <ListItemText primary={yearLabel(y)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size='small' sx={{ minWidth: 230 }}>
          <InputLabel>Demerit criteria</InputLabel>
          <Select
            label='Demerit criteria'
            value={band?.code || ''}
            onChange={(e) => setBandCode(e.target.value)}
          >
            {bands.map((b) => (
              <MenuItem key={b.code} value={b.code}>
                {b.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <ToggleButtonGroup
          size='small'
          exclusive
          value={view}
          onChange={(_, v) => v && setView(v)}
          sx={{ ml: 'auto' }}
        >
          <ToggleButton value='table' sx={{ px: 1.5, textTransform: 'none' }}>
            Table
          </ToggleButton>
          <ToggleButton value='charts' sx={{ px: 1.5, textTransform: 'none' }}>
            Charts
          </ToggleButton>
        </ToggleButtonGroup>
        <Box
          sx={{
            px: 2,
            py: 0.75,
            borderRadius: '10px',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            textAlign: 'center'
          }}
        >
          <Typography sx={{ fontSize: 11 }}>Students with {band?.label}</Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1 }}>{shown.length}</Typography>
        </Box>
      </Box>

      {zeroRows.length > 0 ? (
        <Box
          role='alert'
          sx={{
            p: 1.5,
            borderRadius: '10px',
            border: '1px solid #e8b4af',
            bgcolor: '#fbe3e0',
            color: '#7d1d16'
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
            Zero tolerance this year: {zeroRows.length} record{zeroRows.length === 1 ? '' : 's'} (immediate
            expulsion, whatever the points)
          </Typography>
          {zeroRows.map((z, i) => (
            <Typography key={`${z.student_code}-${z.latest_demerit_date}-${i}`} sx={{ fontSize: 12.5 }}>
              <b>{orDash(z.name)}</b> ({z.student_code}
              {z.year_level ? `, ${yearLabel(z.year_level)}` : ''}
              {z.form_group ? `, ${z.form_group}` : ''}) · {orDash(z.reason)} ·{' '}
              {orDash(z.latest_demerit_date)}
            </Typography>
          ))}
        </Box>
      ) : null}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted }}>
          {category} · counted on its own, never added to the other category:
        </Typography>
        <Chip
          size='small'
          label={`${levelCount.contract} at contract level (${escalation.contract}–${escalation.expulsion - 1})`}
          sx={{ bgcolor: LEVEL_CHIP.contract.bg, color: LEVEL_CHIP.contract.color }}
        />
        <Chip
          size='small'
          label={`${levelCount.expulsion} at expulsion level (${escalation.expulsion}+)`}
          sx={{ bgcolor: LEVEL_CHIP.expulsion.bg, color: LEVEL_CHIP.expulsion.color }}
        />
      </Box>

      {view === 'charts' ? (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chart
            title={`${category} · students with ${band?.label || ''}, by year level${years.length ? ` (${years.map(yearLabel).join(', ')})` : ''}`}
            option={barOption}
            empty={byYear.length === 0}
          />
          <Chart
            title={`${categories.join(' and ')} · points per month, this year vs last year${years.length ? ` (${years.map(yearLabel).join(', ')})` : ''}`}
            option={lineOption}
            empty={monthly.empty}
          />
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            border: `1px solid ${SUMMARY_PALETTE.grid}`,
            borderRadius: '8px'
          }}
        >
          {shown.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: SUMMARY_PALETTE.muted, p: 3, textAlign: 'center' }}>
              No student matches these filters.
            </Typography>
          ) : (
            <Table size='small' stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={HEAD}>Student ID</TableCell>
                  <TableCell sx={HEAD}>Student name</TableCell>
                  <TableCell sx={HEAD}>Year level</TableCell>
                  <TableCell sx={HEAD}>Homeroom</TableCell>
                  <TableCell sx={HEAD}>Demerit points</TableCell>
                  <TableCell sx={HEAD}>Level</TableCell>
                  <TableCell sx={HEAD}>Latest demerit</TableCell>
                  {hasContract ? <TableCell sx={HEAD}>Contract date</TableCell> : null}
                  {hasContract ? <TableCell sx={HEAD}>Contract</TableCell> : null}
                  {hasContract ? <TableCell sx={HEAD}>Points after contract</TableCell> : null}
                  {hasPrevYear ? (
                    <TableCell sx={HEAD}>{`Previous year ${options.previousYearThreshold || 25}+`}</TableCell>
                  ) : null}
                  <TableCell sx={HEAD} />
                </TableRow>
              </TableHead>
              <TableBody>
                {shown.map((r) => {
                  // a contract AND points since it: needs attention (orange, the closed palette's "negative")
                  const flagged = r.contract_date && num(r.points_after_contract) > 0
                  return (
                    <TableRow
                      key={`${r.student_code}-${r.category}`}
                      sx={flagged ? { bgcolor: TONE_PILL.negative.bg } : null}
                    >
                      <TableCell sx={CELL}>{r.student_code}</TableCell>
                      <TableCell sx={CELL}>{orDash(r.name)}</TableCell>
                      <TableCell sx={CELL}>{r.year_level ? yearLabel(r.year_level) : '–'}</TableCell>
                      <TableCell sx={CELL}>{orDash(r.form_group)}</TableCell>
                      <TableCell sx={{ ...CELL, fontWeight: 700 }}>{r.points}</TableCell>
                      <TableCell sx={CELL}>
                        {LEVEL_CHIP[r.escalation] ? (
                          <Chip
                            size='small'
                            label={LEVEL_CHIP[r.escalation].label}
                            sx={{
                              height: 20,
                              bgcolor: LEVEL_CHIP[r.escalation].bg,
                              color: LEVEL_CHIP[r.escalation].color
                            }}
                          />
                        ) : (
                          '–'
                        )}
                      </TableCell>
                      <TableCell sx={CELL}>{orDash(r.latest_demerit_date)}</TableCell>
                      {hasContract ? <TableCell sx={CELL}>{orDash(r.contract_date)}</TableCell> : null}
                      {hasContract ? (
                        <TableCell sx={CELL}>
                          {CONTRACT_CHIP[r.contract_status] ? (
                            <Chip
                              size='small'
                              label={
                                r.contract_follow_up_date
                                  ? `${CONTRACT_CHIP[r.contract_status].label} · until ${r.contract_follow_up_date}`
                                  : CONTRACT_CHIP[r.contract_status].label
                              }
                              sx={{
                                height: 20,
                                bgcolor: CONTRACT_CHIP[r.contract_status].bg,
                                color: CONTRACT_CHIP[r.contract_status].color
                              }}
                            />
                          ) : (
                            '–'
                          )}
                        </TableCell>
                      ) : null}
                      {hasContract ? (
                        <TableCell sx={CELL}>{orDash(r.points_after_contract)}</TableCell>
                      ) : null}
                      {hasPrevYear ? (
                        <TableCell sx={CELL}>
                          {r.prev_year_over ? `Yes (${r.prev_year_points})` : '–'}
                        </TableCell>
                      ) : null}
                      <TableCell sx={CELL}>
                        <Button size='small' variant='outlined' onClick={() => setDetail(r)}>
                          Detail
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </Box>
      )}

      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} maxWidth='sm' fullWidth>
        <DialogTitle sx={{ fontSize: 16 }}>
          {detail
            ? `${orDash(detail.name)}${detail.form_group ? ` (${detail.form_group})` : ''} · ${detail.category} demerits this year (${detail.points} points)`
            : ''}
        </DialogTitle>
        <DialogContent>
          {parseHistory(detail?.history).length > 1 ? (
            <Box sx={{ height: 200, mb: 2 }}>
              <ReactECharts option={studentOption} notMerge style={{ height: '100%', width: '100%' }} />
            </Box>
          ) : null}
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell sx={HEAD}>Date</TableCell>
                <TableCell sx={HEAD}>Reason</TableCell>
                <TableCell sx={HEAD}>Teacher</TableCell>
                <TableCell sx={HEAD}>Points</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {parseHistory(detail?.history).map((h, i) => (
                <TableRow key={`${h.date}-${i}`}>
                  <TableCell sx={CELL}>{h.date}</TableCell>
                  <TableCell sx={CELL}>{orDash(h.reason)}</TableCell>
                  <TableCell sx={CELL}>{orDash(h.staff)}</TableCell>
                  <TableCell sx={CELL}>{orDash(h.points)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default DemeritAlertCard
